import React from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import { ResponsiveBar } from "@nivo/bar";
import { Table } from "semantic-ui-react";
import {
  CHART_THEME,
  LEGEND_CONFIG,
  createFocusableBarLayer,
  getChartContainerProps,
  getAccessibleTextColor
} from "./chartUtils";

const StackedBarByYear = ({
  barGraphDataByYear,
  statusKeys,
  handleChartClick,
  graphTextColor,
  showDataTables,
  styles
}) => {
  const reversedKeys = [...statusKeys].reverse();

  const totalRecommendations = _.sumBy(barGraphDataByYear, "totalCount");

  const firstYear = _.first(barGraphDataByYear).year;
  const lastYear = _.last(barGraphDataByYear).year;
  const yearRange = `${firstYear} to ${lastYear}`;

  const ariaLabel = `Interactive bar chart showing ${totalRecommendations} recommendations by year and status. Tab through individual bars to filter data. Press enter to populate the Recommendation List with corresponding bar graph data segment.`;

  return (
    <>
      <div {...getChartContainerProps()} aria-label={ariaLabel}>
        <ResponsiveBar
          data={barGraphDataByYear}
          labelTextColor={({ color }) =>
            color ? getAccessibleTextColor(color) : graphTextColor || "#000000"
          }
          keys={reversedKeys}
          indexBy="year"
          margin={{ top: 50, right: 200, bottom: 50, left: 60 }}
          padding={0.2}
          colors={({ id, data }) => data.colors[id]}
          borderWidth={1.5}
          theme={CHART_THEME}
          legends={[LEGEND_CONFIG]}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: "# Recommendations",
            legendPosition: "middle",
            legendOffset: -50
          }}
          onClick={e => handleChartClick(e, "status", "year")}
          // Custom layer for keyboard accessibility
          layers={[
            "grid",
            "axes",
            "bars",
            "markers",
            "legends",
            "annotations",
            createFocusableBarLayer(
              statusKeys,
              handleChartClick,
              "status",
              "year",
              {
                sortBy: ["data.indexValue"],
                order: ["asc"],
                statusOrder: "desc"
              }
            )
          ]}
        />
      </div>
      <div css={styles} className={showDataTables ? "" : "sr-only"}>
        <Table
          celled
          compact
          basic="very"
          id="chart-data-table"
          aria-label="Recommendations data by year and status"
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell
                colSpan={statusKeys.length + 2}
                textAlign="center"
                scope="col"
              >
                Detailed Breakdown of {totalRecommendations} Recommendations by
                Year and Status from {yearRange}
              </Table.HeaderCell>
            </Table.Row>
            <Table.Row>
              <Table.HeaderCell scope="col">Year</Table.HeaderCell>
              {statusKeys.map(status => (
                <Table.HeaderCell key={status} textAlign="center">
                  {status}
                </Table.HeaderCell>
              ))}
              <Table.HeaderCell textAlign="center" scope="col">
                Total
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {barGraphDataByYear.map(yearData => {
              const yearTotal = statusKeys.reduce(
                (sum, status) => sum + (yearData[status] || 0),
                0
              );
              return (
                <Table.Row key={yearData.year}>
                  <Table.HeaderCell scope="row">
                    {yearData.year}
                  </Table.HeaderCell>
                  {statusKeys.map(status => (
                    <Table.Cell key={status} textAlign="center">
                      {yearData[status] || 0}
                    </Table.Cell>
                  ))}
                  <Table.Cell textAlign="center">{yearTotal}</Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      </div>
    </>
  );
};

StackedBarByYear.propTypes = {
  barGraphDataByYear: PropTypes.arrayOf(
    PropTypes.shape({
      year: PropTypes.string.isRequired
    })
  ).isRequired,
  handleChartClick: PropTypes.func.isRequired,
  statusKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  graphTextColor: PropTypes.string,
  showDataTables: PropTypes.bool.isRequired,
  styles: PropTypes.shape({})
};

StackedBarByYear.defaultProps = {
  graphTextColor: null,
  styles: {}
};

export default StackedBarByYear;
